import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  deleteWorkpaper,
  downloadWorkpaper,
  getJob,
  listWorkpapers,
  saveBlob,
  startWorkpaper,
} from "../lib/api";
import type { Client, Job } from "../lib/api";
import { formatWhen, formatWhenLong } from "../lib/format";
import { TrashIcon } from "./Icons";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const shortMonth = (d: Date) => d.toLocaleString("en-AU", { month: "short", timeZone: "UTC" });

/** Quarters are the common case, so the period defaults to the last completed one. */
function lastCompletedQuarter(today = new Date()): { start: string; end: string; label: string } {
  const month = today.getUTCMonth();
  const year = today.getUTCFullYear();
  // Australian BAS quarters end Sep, Dec, Mar and Jun.
  const endMonth = Math.floor(month / 3) * 3 - 1;
  const endDate = new Date(Date.UTC(year, endMonth + 1, 0));
  const startDate = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() - 2, 1));
  return {
    start: iso(startDate),
    end: iso(endDate),
    label: `${shortMonth(startDate)}–${shortMonth(endDate)} ${endDate.getUTCFullYear()}`,
  };
}

/** How often to ask again while a job runs. Generation takes minutes. */
const POLL_MS = 3000;

/**
 * When a workpaper was produced.
 *
 * A finished job reports when it finished; one that failed or is still running
 * has only a request time, and saying "requested" rather than "generated" is
 * the difference between a timestamp and a misleading one.
 */
function whenOf(job: Job): { at: number; label: string } {
  return job.completed_at
    ? { at: job.completed_at, label: "Generated" }
    : { at: job.created_at, label: "Requested" };
}

function Timestamp({ job }: { job: Job }) {
  const { at, label } = whenOf(job);
  if (!at) return null;
  return (
    <span className="muted" title={`${label} ${formatWhenLong(at)}`}>
      {label} {formatWhen(at)}
    </span>
  );
}

export function GenerateWorkpaper({ client, admin }: { client: Client; admin: boolean }) {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState(lastCompletedQuarter);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [history, setHistory] = useState<Job[] | null>(null);
  const timer = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Poll until the job reaches a terminal state, then stop. Leaving the timer
  // running would keep asking about every finished job on the page.
  useEffect(() => {
    if (!job || job.is_terminal || !token) return;

    timer.current = window.setTimeout(() => {
      void (async () => {
        try {
          setJob(await getJob(token, job.job_id));
        } catch (err) {
          setError(err instanceof Error ? err.message : "Lost track of the job");
        }
      })();
    }, POLL_MS);

    return stopPolling;
  }, [job, token, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    try {
      const past = await listWorkpapers(token, client.client_id);
      setHistory(past);
    } catch {
      // History is useful, not essential: a client that has never had a
      // workpaper generated is the common case on a new install.
      setHistory([]);
    }
  }, [token, client.client_id]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void loadHistory();
  }, [loadHistory]);

  // A finished run belongs in the history, not only in this component's state.
  // Same reasoning as the mount effect: loadHistory only sets state after its
  // await, so there is no synchronous cascading render.
  useEffect(() => {
    if (!job?.is_terminal) return;
    // oxlint-disable-next-line react/set-state-in-effect
    void loadHistory();
  }, [job?.is_terminal, loadHistory]);

  async function start() {
    if (!token) return;
    setError(null);
    try {
      setJob(
        await startWorkpaper(token, {
          client_id: client.client_id,
          period_start: period.start,
          period_end: period.end,
          period_label: period.label,
          basis: client.gst_basis,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start generation");
    }
  }

  async function download(jobId: string, periodLabel: string) {
    if (!token) return;
    setDownloading(jobId);
    setError(null);
    try {
      // Fetched with the access token, so the download is authenticated like
      // every other call, then saved from memory. Nothing shareable is
      // produced and no URL lands in browser history.
      const { blob, filename } = await downloadWorkpaper(
        token,
        jobId,
        // Only used if the server's name cannot be read. Named after the
        // client either way, so two downloads never collide.
        `${periodLabel} BAS Workpaper - ${client.name}.xlsx`,
      );
      saveBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download the workpaper");
    } finally {
      setDownloading(null);
    }
  }

  async function remove(jobId: string) {
    if (!token) return;
    setDeleting(jobId);
    setError(null);
    try {
      await deleteWorkpaper(token, jobId);
      setConfirming(null);
      // Clear the panel if it was the run being shown, so a deleted workpaper
      // cannot be left on screen with a live Download button.
      setJob((current) => (current?.job_id === jobId ? null : current));
      await loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the workpaper");
    } finally {
      setDeleting(null);
    }
  }

  if (!client.xero.connected) {
    return <span className="muted">Connect Xero first</span>;
  }

  const past = (history ?? []).filter((h) => h.job_id !== job?.job_id);

  function deleteControls(entry: Job) {
    if (!admin) return null;
    if (confirming === entry.job_id) {
      return (
        <>
          <span className="muted">Delete?</span>
          <button
            className="link danger"
            disabled={deleting === entry.job_id}
            onClick={() => void remove(entry.job_id)}
          >
            {deleting === entry.job_id ? "Deleting…" : "Yes, delete"}
          </button>
          <button className="link" onClick={() => setConfirming(null)}>
            Keep
          </button>
        </>
      );
    }
    return (
      <button
        className="icon danger"
        aria-label={`Delete the ${entry.period} workpaper`}
        title="Delete this workpaper"
        onClick={() => setConfirming(entry.job_id)}
      >
        <TrashIcon />
      </button>
    );
  }

  const historyList = past.length > 0 && (
    <ul className="history">
      {past.map((entry) => (
        <li key={entry.job_id}>
          <span className={`tag ${entry.status === "failed" ? "bad" : "ok"}`}>
            {entry.status === "failed" ? "Failed" : entry.period}
          </span>
          {entry.status === "succeeded" ? (
            <button
              className="link"
              disabled={downloading === entry.job_id}
              onClick={() => void download(entry.job_id, entry.period)}
            >
              {downloading === entry.job_id ? "Preparing…" : "Download"}
            </button>
          ) : (
            <span className="muted">{entry.period}</span>
          )}
          <Timestamp job={entry} />
          <span className="spacer" />
          {deleteControls(entry)}
        </li>
      ))}
    </ul>
  );

  if (job) {
    const tone = job.status === "failed" ? "bad" : job.is_terminal ? "ok" : "";
    return (
      <div className="job">
        <span className={`tag ${tone}`}>{job.progress}</span>
        {!job.is_terminal && <span className="muted"> {job.period}</span>}
        {job.status === "failed" && <div className="field-error">{job.error}</div>}
        {job.status === "succeeded" && (
          <>
            <button
              className="primary"
              disabled={downloading === job.job_id}
              onClick={() => void download(job.job_id, job.period)}
            >
              {downloading === job.job_id ? "Preparing…" : "Download"}
            </button>
            <div className="muted">
              {job.period} &middot; {String(job.stats?.exceptions ?? 0)} exception(s)
            </div>
          </>
        )}
        {job.is_terminal && (
          <div className="row">
            <Timestamp job={job} />
            <span className="spacer" />
            {deleteControls(job)}
          </div>
        )}
        <button className="link" onClick={() => setJob(null)}>
          {job.is_terminal ? "Generate another" : "Hide"}
        </button>
        {error && <div className="field-error">{error}</div>}
        {historyList}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="job">
        <button onClick={() => setOpen(true)}>Generate workpaper</button>
        {error && <div className="field-error">{error}</div>}
        {historyList}
      </div>
    );
  }

  return (
    <div className="job">
      <label>
        <span className="muted">From </span>
        <input
          type="date"
          aria-label="Period start"
          value={period.start}
          onChange={(e) => setPeriod({ ...period, start: e.target.value })}
        />
      </label>
      <label>
        <span className="muted"> to </span>
        <input
          type="date"
          aria-label="Period end"
          value={period.end}
          onChange={(e) => setPeriod({ ...period, end: e.target.value })}
        />
      </label>
      <span className="tag">{client.gst_basis} basis</span>
      <button className="primary" onClick={() => void start()}>
        Generate
      </button>
      <button className="link" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <div className="field-error">{error}</div>}
      {historyList}
    </div>
  );
}
